// src/Pages/Blog/PlaceholderImage.jsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  AutoAwesome as AIIcon,
  TrendingUp as GrowthIcon,
  Handshake as TrustIcon,
  Campaign as MarketingIcon,
  Article as BlogIcon,
  BusinessCenter as BusinessIcon,
  Psychology as InsightIcon,
  Groups as CommunityIcon
} from '@mui/icons-material';

/**
 * Generates a placeholder image for blog posts without a hero image
 * Uses category-based colors and icons for visual variety
 */
const PlaceholderImage = ({ title, categories = [], height = 400 }) => {
  // Color schemes based on common blog categories
  const colorSchemes = {
    'AI Automation': {
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      icon: AIIcon,
      color: '#667eea'
    },
    'Business Growth': {
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      icon: GrowthIcon,
      color: '#f093fb'
    },
    'Trust Networks': {
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      icon: TrustIcon,
      color: '#4facfe'
    },
    'Marketing': {
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      icon: MarketingIcon,
      color: '#fa709a'
    },
    'SEO': {
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      icon: InsightIcon,
      color: '#30cfd0'
    },
    'Customer Service': {
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      icon: CommunityIcon,
      color: '#a8edea'
    },
    'Social Media': {
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      icon: MarketingIcon,
      color: '#ffecd2'
    },
    'Local Business': {
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      icon: BusinessIcon,
      color: '#ff9a9e'
    },
    'default': {
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      icon: BlogIcon,
      color: '#667eea'
    }
  };

  // Determine which color scheme to use based on first category
  const category = categories[0] || 'default';
  const scheme = colorSchemes[category] || colorSchemes['default'];
  const Icon = scheme.icon;

  // Extract first letter or word from title for additional variety
  const titleInitial = title ? title.charAt(0).toUpperCase() : 'R';
  const firstWord = title ? title.split(' ')[0] : 'Revfer';

  return (
    <Box
      sx={{
        width: '100%',
        height: height,
        background: scheme.gradient,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        color: 'white'
      }}
    >
      {/* Decorative background circles */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          top: '-100px',
          right: '-100px'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          bottom: '-50px',
          left: '-50px'
        }}
      />

      {/* Main icon */}
      <Icon 
        sx={{ 
          fontSize: { xs: 80, md: 120 }, 
          mb: 2, 
          opacity: 0.9,
          zIndex: 1
        }} 
      />

      {/* Title or category */}
      <Typography
        variant="h3"
        sx={{
          fontSize: { xs: '1.5rem', md: '2.5rem' },
          fontWeight: 700,
          textAlign: 'center',
          px: 4,
          maxWidth: '80%',
          textShadow: '0 2px 4px rgba(0,0,0,0.2)',
          zIndex: 1,
          mb: 1
        }}
      >
        {firstWord}
      </Typography>

      {/* Category badge */}
      {category !== 'default' && (
        <Typography
          variant="body2"
          sx={{
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            px: 2,
            py: 0.5,
            borderRadius: '20px',
            fontSize: '0.9rem',
            fontWeight: 500,
            zIndex: 1
          }}
        >
          {category}
        </Typography>
      )}

      {/* Revfer watermark */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          opacity: 0.5,
          fontSize: '0.8rem',
          zIndex: 1
        }}
      >
        Revfer Blog
      </Typography>
    </Box>
  );
};

export default PlaceholderImage;